import {
  button,
  div,
  figure,
  goTo,
  img,
  input,
  span
} from '@srfnstack/fntags'
import { CreditButton } from '../../components/CreditButton.js'
import { StarRating } from '../../components/StarRating.js'

const toTitle = s => s.replace(/\b\w/g, c => c.toUpperCase())

export const AssetGrid = ({ assets, selectedAsset, selectedAssets, showRejected, loading, toggleReject, handlePromote, handleEnhance, handleBulkPromote, handleBulkReject, productTypes }) =>
  div({ class: 'flex-grow p-4 relative' },
    assets.bindAs(all => {
      const pending = all.filter(asset => {
        return asset.type === 'PENDING_JOB' || asset.status === 'PENDING'
      })
      if (pending.length === 0) return span()

      const completed = all.filter(asset => {
        return asset.status === 'COMPLETED'
      }).length
      const total = all.length
      const percent = Math.round((completed / total) * 100)

      return div({ class: 'mb-6 p-4 bg-primary/5 rounded-xl border border-primary/20 flex flex-col gap-3' },
        div({ class: 'flex justify-between items-end' },
          div({ class: 'flex flex-col gap-1' },
            span({ class: 'text-xs font-bold uppercase tracking-widest opacity-60' }, 'Production Pipeline Active'),
            span({ class: 'text-sm font-medium' }, `Generating ${total} assets...`)
          ),
          span({ class: 'text-2xl font-black text-primary' }, `${percent}%`)
        ),
        div({ class: 'w-full bg-base-300 h-2 rounded-full overflow-hidden' },
          div({
            class: 'bg-primary h-full transition-all duration-500 ease-out',
            style: { width: `${percent}%` }
          })
        ),
        div({ class: 'flex justify-between text-[10px] font-bold uppercase opacity-40' },
          span(`${completed} completed`),
          span(`${total - completed} remaining`)
        )
      )
    }),
    selectedAssets.bindAs(sel => {
      if (sel.length === 0) return div()
      return div({ class: 'fixed bottom-12 left-1/2 -translate-x-1/2 bg-base-300 p-4 rounded-2xl shadow-2xl flex gap-4 items-center z-[150] border border-primary/30 backdrop-blur-md' },
        span({ class: 'font-bold text-sm px-2' }, `${sel.length} items`),
        button({ class: 'btn btn-primary btn-sm', onclick: handleBulkPromote }, 'Bulk Promote'),
        button({ class: 'btn btn-error btn-sm', onclick: handleBulkReject }, 'Reject All'),
        button({
          class: 'btn btn-ghost btn-sm btn-circle',
          onclick: () => selectedAssets([]),
          title: 'Clear Selection'
        }, '✕')
      )
    }),
    loading.bindAs(l => l
      ? div({ class: 'flex items-center justify-center h-64' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
      : showRejected.bindAs(show =>
        assets.bindChildren(
          div({ class: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' }),
          (aState) => {
            const assetIdParam = new URLSearchParams(window.location.search).get('assetId')
            if (assetIdParam && aState().id?.toString() === assetIdParam && !selectedAsset()) {
              selectedAsset(aState())
            }
            return aState.bindAs(a => {
              if (a.type === 'PENDING_JOB' || a.status === 'PENDING') {
                return div({
                  class: 'card bg-base-200 shadow-sm border border-base-300 relative overflow-hidden aspect-square'
                },
                div({ class: 'p-2 bg-base-300/50 flex flex-wrap gap-2 items-center border-b border-base-300' },
                  span({ class: 'text-[10px] font-bold opacity-40 italic' }, a.type === 'PENDING_JOB' ? `Job #${a.job_id}` : `Asset #${a.id}`),
                  (a.source_asset_id || a.parent_id) ? span({ class: 'badge badge-secondary badge-xs font-bold' }, `Enhancement (from #${a.source_asset_id || a.parent_id})`) : ''
                ),
                div({ class: 'flex-grow flex flex-col items-center justify-center gap-4 bg-base-200/80' },
                  span({ class: 'loading loading-spinner loading-lg text-primary' }),
                  span({ class: 'text-xs font-bold opacity-50 uppercase tracking-widest' },
                    (a.job_type === 'IMAGE_ENHANCE' || a.type === 'UPSCALE' || a.parent_id) ? 'Enhancing...' : 'Generating...'
                  )
                )
                )
              }

              if (!show && a.rejected) return span({ style: { display: 'none' } })

              const isSelected = selectedAssets.bindAttr(sel => sel.includes(a.id))

              return div({
                class: selectedAssets.bindAttr(sel => `card bg-base-200 shadow-sm border ${sel.includes(a.id) ? 'border-primary ring-2 ring-primary/20' : 'border-base-300'} relative cursor-pointer hover:border-primary transition-all group overflow-hidden`),
                onclick: () => selectedAsset(aState())
              },
              div({ class: 'p-2 bg-base-300/50 flex justify-between items-center border-b border-base-300' },
                div({ class: 'flex gap-2 items-center' },
                  input({
                    type: 'checkbox',
                    class: 'checkbox checkbox-xs checkbox-primary',
                    checked: isSelected,
                    onclick: (e) => {
                      e.stopPropagation()
                      const sel = selectedAssets()
                      if (sel.includes(a.id)) selectedAssets(sel.filter(id => id !== a.id))
                      else selectedAssets([...sel, a.id])
                    }
                  }),
                  span({ class: 'text-[10px] font-bold opacity-60' }, `#${a.id}`)
                ),
                a.parent_id ? span({ class: 'badge badge-secondary badge-xs font-bold' }, 'En') : ''
              ),
              figure({ class: 'relative aspect-square' },
                img({
                  src: `/api/image?file=${encodeURIComponent(a.file_path)}`,
                  class: 'object-cover w-full h-full transition-transform group-hover:scale-105' + (a.rejected ? ' grayscale opacity-50' : '')
                }),
                a.rejected ? div({ class: 'absolute inset-0 bg-base-300/20 flex items-center justify-center' }, span({ class: 'badge badge-error' }, 'Rejected')) : '',
                button({
                  class: `absolute top-2 right-2 btn btn-circle btn-xs ${a.rejected ? 'btn-success' : 'btn-error'} opacity-0 group-hover:opacity-100 transition-opacity z-10`,
                  onclick: (e) => {
                    e.stopPropagation()
                    toggleReject(aState)
                  },
                  title: a.rejected ? 'Unreject Image' : 'Reject Image'
                }, a.rejected ? '➕' : '❌')
              ),
              div({ class: 'card-body p-2 bg-base-100', onclick: (e) => e.stopPropagation() },
                a.parent_id
                  ? div({ class: 'mb-2 space-y-1' },
                    a.params?.user_correction
                      ? div({ class: 'text-[10px] italic opacity-70 line-clamp-1' }, span({ class: 'font-bold' }, 'Correction: '), a.params.user_correction)
                      : '',
                    a.params?.architect_prompt
                      ? div({ class: 'text-[10px] opacity-60 line-clamp-2 leading-tight' }, span({ class: 'font-bold' }, 'AI Prompt: '), a.params.architect_prompt)
                      : '',
                    a.params?.mask_path
                      ? button({
                        class: 'btn btn-ghost btn-xs text-primary p-0 h-auto min-h-0',
                        onclick: () => {
                          selectedAsset(aState())
                          setTimeout(() => window.__showMaskOverlay(a.params.mask_path), 100)
                        }
                      }, 'View Mask')
                      : ''
                  )
                  : '',
                div({ class: 'flex justify-center mb-1' },
                  StarRating({ assetId: a.id, initial: a.rating })
                ),
                div({ class: 'card-actions justify-center items-center mt-auto gap-2' },
                  button({
                    class: 'btn btn-ghost btn-xs px-1',
                    title: 'Paint Mask',
                    onclick: () => {
                      selectedAsset(aState())
                      if (!window.__isMasking()) window.__toggleMasking()
                    }
                  }, span({ class: 'material-symbols-rounded !text-[18px]' }, 'brush')),
                  CreditButton({
                    actionCode: 'IMAGE_ENHANCE',
                    class: 'btn btn-ghost btn-xs px-1',
                    title: 'Global Enhance',
                    onclick: async () => {
                      handleEnhance(aState())
                    },
                    label: span({ class: 'material-symbols-rounded !text-[18px]' }, 'magic_button')
                  }),
                  productTypes.bindAs(types => {
                    const isIsolated = a.background_type === 'ISOLATED_BLACK' || a.background_type === 'ISOLATED_WHITE'
                    const compatible = isIsolated
                      ? types
                      : types.filter(t => (t.product_detail_config?.artwork_class || 'SURFACE_ART') !== 'ISOLATED_ART')
                    const promotedMap = new Map((a.product_concepts || []).map(pc => [pc.product_type.toLowerCase(), pc.id]))
                    const toPromote = compatible.filter(t => !promotedMap.has(t.keyword.toLowerCase()))

                    return div({ class: 'flex flex-wrap gap-1 justify-center' },
                      ...[...promotedMap.entries()].map(([type, id]) =>
                        button({
                          class: 'btn btn-success btn-xs',
                          onclick: (e) => { e.stopPropagation(); goTo(`/product-review/${id}`) }
                        }, toTitle(type))
                      ),
                      ...toPromote.map(type =>
                        CreditButton({
                          actionCode: 'IMAGE_UPSCALE',
                          class: 'btn btn-primary btn-xs',
                          onclick: async () => handlePromote(a.id, type.keyword),
                          label: toTitle(type.keyword)
                        })
                      )
                    )
                  })
                )
              )
              )
            })
          }
        )
      )
    )
  )
